import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiChevronDown, FiAlertCircle } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RAZORPAY_KEY_ID, initializeRazorpay } from '../../config/razorpay';
import { userApi } from '../../api/userApi';

const Checkout = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRazorpayReady, setIsRazorpayReady] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [useExistingAddress, setUseExistingAddress] = useState(true); // Set to true by default
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    streetAddress: '',
    apartment: '',
    city: '',
    state: '',
    pinCode: '',
    orderNotes: '',
    agreeToTerms: false,
    country: 'India'
  });
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState({
    address_type: 'home',
    first_name: '',
    last_name: '',
    company_name: '',
    country: 'India',
    street_address_1: '',
    street_address_2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    order_notes: '',
    is_default: false
  });

  const emptyFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    streetAddress: '',
    apartment: '',
    city: '',
    state: '',
    pinCode: '',
    orderNotes: '',
    agreeToTerms: false,
    country: 'India'
  };

  // Add formatPrice function
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Add this function to handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUseExistingAddressChange = (e) => {
    const useExisting = e.target.checked;
    setUseExistingAddress(useExisting);
    
    if (useExisting && savedAddresses.length > 0) {
      // If switching to use existing address, load the selected address
      const addressToUse = savedAddresses.find(addr => addr.id === selectedAddressId) || savedAddresses[0];
      handleAddressSelect(addressToUse.id);
    } else {
      // If switching to new address, clear all fields
      setFormData(emptyFormData);
      setSelectedAddressId(null);
    }
  };

  // Add this function at the top of your component
  const calculateDiscount = (price, activeOffers) => {
    if (!activeOffers || activeOffers.length === 0) return 0;

    // Calculate discounts from all applicable offers
    const discounts = activeOffers.map(offer => {
      if (offer.offer_type === 'PERCENTAGE') {
        return (price * parseFloat(offer.discount_value)) / 100;
      } else if (offer.offer_type === 'FIXED') {
        return parseFloat(offer.discount_value);
      }
      return 0;
    });

    // Return the highest discount
    return Math.max(...discounts);
  };

  // Update the totals calculation
  const calculateTotals = (items) => {
    return items.reduce((acc, item) => {
      const itemOriginalPrice = item.price;
      const itemFinalPrice = item.final_price;
      const itemQuantity = item.quantity;
      
      const itemSubtotal = itemOriginalPrice * itemQuantity;
      const itemTotal = itemFinalPrice * itemQuantity;
      const itemDiscount = itemSubtotal - itemTotal;
      
      return {
        subtotal: acc.subtotal + itemSubtotal,
        totalDiscount: acc.totalDiscount + itemDiscount,
        total: acc.total + itemTotal
      };
    }, {
      subtotal: 0,
      totalDiscount: 0,
      total: 0
    });
  };

  // Update the existing calculations
  const { subtotal, totalDiscount, total } = calculateTotals(cartItems);

  const retryOperation = async (operation, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await operation();
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
  };

  useEffect(() => {
    // Load Razorpay
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      setIsRazorpayReady(true);
    };
    document.body.appendChild(script);

    // Load cart items
    const fetchCartItems = async () => {
      try {
        setIsLoading(true);
        const items = await userApi.getCartItems();
        console.log('Raw cart items:', items); // Debug log
        const itemsWithPrices = items.map(item => {
          const transformed = {
            ...item,
            price: parseFloat(item.originalPrice || 0),
            final_price: parseFloat(item.finalPrice || 0),
            activeOffers: item.activeOffers || []
          };
          console.log('Transformed item:', transformed); // Debug log
          return transformed;
        });
        setCartItems(itemsWithPrices);
      } catch (error) {
        console.error('Error fetching cart items:', error);
        if (error.message === 'Please login to view cart') {
          navigate('/login');
          return;
        }
        toast.error('Failed to load cart items');
        setCartItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCartItems();

    return () => {
      // Cleanup script on component unmount
      document.body.removeChild(script);
    };
  }, [navigate]);

  // Add event listener for cart updates
  useEffect(() => {
    const handleCartUpdate = async () => {
      try {
        const items = await userApi.getCartItems();
        setCartItems(items);
      } catch (error) {
        console.error('Error updating cart items:', error);
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  useEffect(() => {
    // Function to fetch addresses
    const fetchAddresses = async () => {
      try {
        const addresses = await userApi.getAddresses();
        setSavedAddresses(addresses);
        
        // If addresses exist, select the default one or the first one
        if (addresses.length > 0) {
          const defaultAddress = addresses.find(addr => addr.is_default) || addresses[0];
          setSelectedAddressId(defaultAddress.id);
          handleAddressSelect(defaultAddress.id, addresses);
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
        toast.error('Failed to load saved addresses');
      }
    };

    fetchAddresses();
  }, []);

  const validateForm = () => {
    // If using a saved address and one is selected, skip detailed validation
    if (useExistingAddress && selectedAddressId) {
      if (!formData.agreeToTerms) {
        toast.error('Please agree to the terms and conditions');
        return false;
      }
      return true;
    }

    // Your existing validation logic for new address entry
    if (!formData.firstName?.trim()) {
      toast.error('Please enter your first name');
      return false;
    }
    // ... rest of your validation logic
  };

  const createOrder = async () => {
    try {
      // Make API call to create order
      const response = await userApi.createOrder({
        amount: Math.round(total * 100),
        currency: "INR",
        receipt: `receipt_${Date.now()}`,
        notes: {
          orderItems: JSON.stringify(cartItems.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity
          })))
        }
      });
      return response;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    
    if (isProcessing) return;

    try {
      setIsProcessing(true);

      const isRazorpayReady = await initializeRazorpay();
      if (!isRazorpayReady) {
        throw new Error('Razorpay SDK failed to load');
      }

      if (!validateForm()) {
        setIsProcessing(false);
        return;
      }

      // Calculate totals including discount
      const { subtotal, totalDiscount, total } = calculateTotals(cartItems);

      const orderResponse = await retryOperation(async () => {
        const response = await userApi.createOrder({
          shipping_address_id: selectedAddressId,
          billing_address_id: selectedAddressId,
          subtotal: subtotal,
          discount: totalDiscount,
          total: total // This should be the final amount after discount
        });
        
        if (!response?.razorpay_order_id) {
          throw new Error('Invalid order response');
        }
        
        return response;
      }, 3);

      const options = {
        key: orderResponse.razorpay_key_id,
        amount: Math.round(total * 100), // Convert to paise and ensure it's the discounted amount
        currency: "INR",
        name: "Panchali Boutique",
        description: `Order Payment - ${orderResponse.items.length} items`,
        order_id: orderResponse.razorpay_order_id,
        prefill: {
          name: `${orderResponse.shipping_address.first_name} ${orderResponse.shipping_address.last_name}`,
          email: orderResponse.shipping_address.email,
          contact: orderResponse.shipping_address.phone
        },
        modal: {
          escape: false,
          ondismiss: function() {
            setIsProcessing(false);
          }
        },
        retry: {
          enabled: true,
          max_count: 3
        },
        notes: {
          address: orderResponse.shipping_address.street_address_1,
          subtotal: subtotal.toString(),
          discount: totalDiscount.toString(),
          total: total.toString()
        },
        theme: {
          color: "#2C3E50"
        },
        handler: async function(response) {
          try {
            const verificationResponse = await userApi.verifyPayment(
              orderResponse.order_number, 
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }
            );

            if (verificationResponse?.payment_status === 'completed') {
              toast.success('Payment successful!');
              navigate('/order-confirmation', { 
                state: { 
                  orderId: orderResponse.order_number,
                  orderDetails: {
                    ...orderResponse,
                    status: verificationResponse.status,
                    payment_status: verificationResponse.payment_status,
                    paymentId: response.razorpay_payment_id
                  }
                }
              });
            } else {
              throw new Error(verificationResponse?.message || 'Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error(error.message || 'Payment verification failed. Please contact support.');
            // Add retry logic for failed verifications
            try {
              await userApi.notifyFailedPayment(orderResponse.order_number, {
                error: error.message,
                payment_id: response.razorpay_payment_id
              });
            } catch (notifyError) {
              console.error('Failed to notify payment failure:', notifyError);
            }
          } finally {
            setIsProcessing(false);
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();

    } catch (error) {
      console.error('Payment initialization error:', error);
      toast.error(error.message || 'Failed to initialize payment');
      setIsProcessing(false);
    }
  };

  const handlePaymentVerification = async (response, orderResponse) => {
    try {
      await userApi.verifyPayment(orderResponse.order_number, response);
      // Handle successful verification
    } catch (error) {
      console.error('Payment verification failed:', error);
      toast.error('Payment verification failed. Please contact support.');
      
      // Remove the notifyFailedPayment call since it's not implemented
      // or implement it in userApi if needed
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddressSelect = (addressId, addressList = savedAddresses) => {
    const selectedAddress = addressList.find(addr => addr.id === addressId);
    if (selectedAddress) {
      setFormData({
        firstName: selectedAddress.first_name,
        lastName: selectedAddress.last_name,
        email: selectedAddress.email,
        phone: selectedAddress.phone,
        streetAddress: selectedAddress.street_address_1,
        apartment: selectedAddress.street_address_2 || '',
        city: selectedAddress.city,
        state: selectedAddress.state,
        pinCode: selectedAddress.pincode,
        country: selectedAddress.country,
        orderNotes: selectedAddress.order_notes || '',
        agreeToTerms: false
      });
      setSelectedAddressId(addressId);
    }
  };

  const handleAddNewAddress = () => {
    setShowAddressModal(true);
  };

  const handleAddressFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    try {
      const newAddress = await userApi.addAddress(addressForm);
      setSavedAddresses(prev => [...prev, newAddress]);
      setShowAddressModal(false);
      toast.success('Address added successfully');
      
      // Automatically select the new address
      setSelectedAddressId(newAddress.id);
      handleAddressSelect(newAddress.id);
      setUseExistingAddress(true);
    } catch (error) {
      console.error('Error adding address:', error);
      toast.error('Failed to add address');
    }
  };

  return (
    <>
      <Helmet>
        <title>Checkout - Your Store Name</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Billing Details */}
          <div className="lg:col-span-7">

            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Saved Addresses</h3>
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    id="useExisting"
                    checked={useExistingAddress}
                    onChange={handleUseExistingAddressChange}
                    className="h-4 w-4 text-[#2C3E50] focus:ring-[#2C3E50] border-gray-300 rounded"
                  />
                  <label htmlFor="useExisting" className="text-sm text-gray-700">
                    Use saved address
                  </label>
                  {!useExistingAddress && (
                    <button
                      onClick={handleAddNewAddress}
                      className="px-4 py-2 bg-[#2C3E50] text-white rounded-lg hover:bg-[#34495E] transition-colors"
                    >
                      Add New Address
                    </button>
                  )}
                </div>
              </div>

              {useExistingAddress && savedAddresses.length > 0 && (
                <div className="space-y-4">
                  {savedAddresses.map((address) => (
                    <label
                      key={address.id}
                      className={`block relative p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedAddressId === address.id
                          ? 'border-[#2C3E50] bg-[#2C3E50]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start">
                        <input
                          type="radio"
                          name="savedAddress"
                          value={address.id}
                          checked={selectedAddressId === address.id}
                          onChange={() => handleAddressSelect(address.id)}
                          className="h-4 w-4 text-[#2C3E50] focus:ring-[#2C3E50] border-gray-300"
                        />
                        <div className="ml-3">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">
                              {address.first_name} {address.last_name}
                            </p>
                            {address.is_default && (
                              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {address.street_address_1}
                            {address.street_address_2 && `, ${address.street_address_2}`}
                          </p>
                          <p className="text-sm text-gray-500">
                            {address.city}, {address.state} {address.pincode}
                          </p>
                          <p className="text-sm text-gray-500">{address.phone}</p>
                          <p className="text-sm text-gray-500">{address.email}</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {useExistingAddress && savedAddresses.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-gray-500">No saved addresses found.</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold mb-6">
                {useExistingAddress ? 'Selected Billing Details' : 'Billing Details'}
              </h2>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={useExistingAddress}
                      required
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                        useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={useExistingAddress}
                      required
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                        useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company name (optional)
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country / Region
                  </label>
                  <input
                    type="text"
                    name="country"
                    value="India"
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                    aria-label="Country is set to India"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street address *
                    </label>
                    <input
                      type="text"
                      name="streetAddress"
                      value={formData.streetAddress}
                      onChange={handleInputChange}
                      placeholder="House number and street name"
                      required
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                        useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="apartment"
                      value={formData.apartment}
                      onChange={handleInputChange}
                      placeholder="Apartment, suite, unit, etc. (optional)"
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                        useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Town / City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PIN Code *
                  </label>
                  <input
                    type="text"
                    name="pinCode"
                    value={formData.pinCode}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order notes (optional)
                  </label>
                  <textarea
                    name="orderNotes"
                    value={formData.orderNotes}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Notes about your order, e.g. special notes for delivery"
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      useExistingAddress ? 'bg-gray-50 cursor-not-allowed' : ''
                    }`}
                  />
                </div>


              </form>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <h2 className="text-2xl font-semibold mb-6">Your Order</h2>
              <div className="space-y-4">
                {/* Cart Items */}
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 py-4 border-b">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="text-sm font-medium">{item.name}</h3>
                      <p className="text-sm text-gray-500">
                        Quantity: {item.quantity}
                      </p>
                      <div className="flex flex-col">
                        {/* Final Price */}
                        <p className="text-sm font-medium text-red-600">
                          ₹{(item.final_price * item.quantity).toLocaleString('en-IN')}
                        </p>
                        
                        {/* Sale Price (if different from final price) */}
                        {item.salePrice > item.final_price && (
                          <p className="text-sm text-gray-500 line-through">
                            ₹{(item.salePrice * item.quantity).toLocaleString('en-IN')}
                          </p>
                        )}
                        
                        {/* Original Price (if different from sale price) */}
                        {item.price > item.salePrice && (
                          <p className="text-sm text-gray-500 line-through">
                            ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                          </p>
                        )}
                        
                        {item.activeOffers && item.activeOffers.length > 0 && (
                          <p className="text-sm text-green-600">
                            Offer Applied
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Order Summary */}
                <div className="space-y-2 pt-4">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  {totalDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-{formatPrice(totalDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-semibold border-t pt-4">
                    <span>Total</span>
                    <span>{formatPrice(total)}</span>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="mt-8">
                  <h3 className="font-medium text-gray-900 mb-4">Payment Method</h3>
                  <div className="space-y-4">
                    <label className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="razorpay"
                        defaultChecked
                        className="h-4 w-4 text-indigo-600"
                      />
                      <span>Razorpay (Cards, UPI, NetBanking)</span>
                    </label>
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="mt-6">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 rounded"
                    />
                    <span className="text-sm text-gray-500">
                      I have read and agree to the website's{' '}
                      <Link 
                        to="/privacy-policy#terms" 
                        target="_blank"
                        rel="noopener noreferrer" 
                        className="text-indigo-600 hover:text-indigo-500"
                      >
                        terms and conditions
                      </Link>
                    </span>
                  </label>
                </div>

                {/* Place Order Button */}
                <button
                  onClick={handlePlaceOrder}
                  disabled={isProcessing || !isRazorpayReady}
                  className={`w-full mt-6 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all
                    ${(isProcessing || !isRazorpayReady)
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-[#2C3E50] hover:bg-[#34495E]'
                    } text-white font-medium`}
                >
                  {isProcessing ? (
                    <>
                      <span className="animate-spin">⌛</span>
                      Processing...
                    </>
                  ) : !isRazorpayReady ? (
                    'Loading Payment System...'
                  ) : (
                    <>
                      <span>Place Order</span>
                      <span>₹{total.toFixed(2)}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Address Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Add New Address</h2>
                <button
                  onClick={() => setShowAddressModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleAddressSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={addressForm.first_name}
                      onChange={handleAddressFormChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={addressForm.last_name}
                      onChange={handleAddressFormChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company name (optional)
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={addressForm.company_name}
                    onChange={handleAddressFormChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country / Region
                  </label>
                  <input
                    type="text"
                    name="country"
                    value="India"
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                    aria-label="Country is set to India"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street address *
                    </label>
                    <input
                      type="text"
                      name="street_address_1"
                      value={addressForm.street_address_1}
                      onChange={handleAddressFormChange}
                      placeholder="House number and street name"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="street_address_2"
                      value={addressForm.street_address_2}
                      onChange={handleAddressFormChange}
                      placeholder="Apartment, suite, unit, etc. (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Town / City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={addressForm.city}
                    onChange={handleAddressFormChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={addressForm.state}
                    onChange={handleAddressFormChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PIN Code *
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    value={addressForm.pincode}
                    onChange={handleAddressFormChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={addressForm.phone}
                    onChange={handleAddressFormChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={addressForm.email}
                    onChange={handleAddressFormChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order notes (optional)
                  </label>
                  <textarea
                    name="order_notes"
                    value={addressForm.order_notes}
                    onChange={handleAddressFormChange}
                    rows="4"
                    placeholder="Notes about your order, e.g. special notes for delivery"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_default"
                    checked={addressForm.is_default}
                    onChange={handleAddressFormChange}
                    className="h-4 w-4 text-indigo-600 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-500">
                    Set as default address
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddressModal(false)}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-[#2C3E50] text-white rounded-lg hover:bg-[#34495E]"
                  >
                    Save Address
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Checkout;
























































